import html2canvas from 'html2canvas';
import JsPDF, { jsPDFOptions } from 'jspdf';
import imageSrc from '@/assets/cascade_event_system_logo.jpg';
import i18n from '@/i18n';
import '@/lib/font/Jost-Regular-normal';
import { Sheet, SheetWithItems } from '@/model/Sheet';

const PDF_WIDTH = 297;
const PDF_HEIGHT = 210;
const PDF_CONTENT_LEFT = 23;
const PDF_CONTENT_RIGHT = 5;
const PDF_CONTENT_TOP = 23;
const PDF_CONTENT_BOTTOM = 5;
const PDF_CONTENT_WIDTH = PDF_WIDTH - PDF_CONTENT_LEFT - PDF_CONTENT_RIGHT;
const PDF_CONTENT_HEIGHT = PDF_HEIGHT - PDF_CONTENT_TOP - PDF_CONTENT_BOTTOM;
const PDF_FOOTER_LEFT = 5;
const PDF_RENDER_SCALE = 2;
const PDF_CONTENT_THRESHOLD = 245;

function getRenderedContentWidth(canvas: HTMLCanvasElement): number {
    const context = canvas.getContext('2d');
    if (!context) {
        return canvas.width;
    }

    let pixels: Uint8ClampedArray;
    try {
        pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    } catch {
        return canvas.width;
    }

    for (let x = canvas.width - 1; x >= 0; x -= 1) {
        for (let y = 0; y < canvas.height; y += 1) {
            const pixelIndex = (y * canvas.width + x) * 4;
            if (
                pixels[pixelIndex] < PDF_CONTENT_THRESHOLD ||
                pixels[pixelIndex + 1] < PDF_CONTENT_THRESHOLD ||
                pixels[pixelIndex + 2] < PDF_CONTENT_THRESHOLD
            ) {
                return Math.min(canvas.width, x + 1 + PDF_RENDER_SCALE * 4);
            }
        }
    }

    return canvas.width;
}

function cropCanvas(
    canvas: HTMLCanvasElement,
    contentWidth: number,
): HTMLCanvasElement {
    if (contentWidth >= canvas.width) {
        return canvas;
    }

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = contentWidth;
    croppedCanvas.height = canvas.height;
    const context = croppedCanvas.getContext('2d');
    if (context) {
        context.drawImage(
            canvas,
            0,
            0,
            contentWidth,
            canvas.height,
            0,
            0,
            contentWidth,
            canvas.height,
        );
    }

    return croppedCanvas;
}

function addSheetPdfHeader(
    pdf: JsPDF,
    sheet: SheetWithItems | Sheet,
    pageNumber: number,
): void {
    const logoWidth = 18;
    const logoHeight = logoWidth / 1.03;
    const logoMargin = 5;
    const logo = new Image();
    logo.src = imageSrc;

    pdf.addImage(
        logo,
        'JPEG',
        PDF_WIDTH - logoMargin - logoWidth,
        logoMargin,
        logoWidth,
        logoHeight,
    );

    pdf.setFontSize(12);
    pdf.text(i18n.t('sheet.pdf.creator').toString(), 17, 20, {
        align: 'left',
        angle: -90,
    });
    pdf.text(i18n.t('sheet.pdf.administrator').toString(), 12, 20, {
        align: 'left',
        angle: -90,
    });
    if (sheet.name && sheet.eventName) {
        pdf.text(
            i18n
                .t('sheet.pdf.sheet-event', {
                    sheetName: sheet.name,
                    eventName: sheet.eventName,
                })
                .toString(),
            7,
            20,
            {
                align: 'left',
                angle: -90,
            },
        );
    }

    pdf.setFontSize(10);
    pdf.text(
        `${pageNumber}`,
        PDF_FOOTER_LEFT,
        PDF_HEIGHT - PDF_CONTENT_BOTTOM,
        {
            align: 'left',
        },
    );
}

export default function generateSheetPdf(
    sheet: SheetWithItems | Sheet,
): Promise<void> {
    const element = document.getElementById('pdf-canvas');
    const elementWidth = element?.scrollWidth ?? 0;
    if (!element || elementWidth <= 0 || element.offsetHeight <= 0) {
        return Promise.resolve();
    }

    const renderPdf = async (): Promise<void> => {
        const measurementCanvas = await html2canvas(element, {
            scale: PDF_RENDER_SCALE,
            x: 0,
            y: 0,
            width: elementWidth,
            height: element.offsetHeight,
            windowWidth: elementWidth,
            windowHeight: element.offsetHeight,
        });
        const contentCanvasWidth = getRenderedContentWidth(measurementCanvas);
        const contentWidth = Math.min(
            elementWidth,
            contentCanvasWidth / PDF_RENDER_SCALE,
        );
        const scale = PDF_CONTENT_WIDTH / contentWidth;
        const pageHeightInCssPixels = PDF_CONTENT_HEIGHT / scale;
        const pageCount = Math.max(
            1,
            Math.ceil(element.offsetHeight / pageHeightInCssPixels),
        );

        const pdfOptions = {
            orientation: 'landscape',
            unit: 'mm',
            format: [PDF_WIDTH, PDF_HEIGHT],
        };
        const pdf = new JsPDF(pdfOptions as jsPDFOptions);
        pdf.setFont('Jost-Regular');

        for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
            const pageTop = pageIndex * pageHeightInCssPixels;
            const pageHeight = Math.min(
                pageHeightInCssPixels,
                element.offsetHeight - pageTop,
            );
            // Rendering must stay sequential to keep memory usage bounded.
            // eslint-disable-next-line no-await-in-loop
            const canvas = await html2canvas(element, {
                scale: PDF_RENDER_SCALE,
                x: 0,
                y: pageTop,
                width: elementWidth,
                height: pageHeight,
                windowWidth: elementWidth,
                windowHeight: element.offsetHeight,
            });

            if (canvas.width <= 0 || canvas.height <= 0) {
                return;
            }

            if (pageIndex > 0) {
                pdf.addPage();
            }

            const croppedCanvas = cropCanvas(canvas, contentCanvasWidth);
            const imageHeight =
                PDF_CONTENT_WIDTH *
                (croppedCanvas.height / croppedCanvas.width);
            pdf.addImage(
                croppedCanvas.toDataURL('image/jpeg'),
                'JPEG',
                PDF_CONTENT_LEFT,
                PDF_CONTENT_TOP,
                PDF_CONTENT_WIDTH,
                imageHeight,
            );

            // Draw the fixed header after the canvas so the wider image cannot
            // cover the vertical metadata, logo, or page number.
            addSheetPdfHeader(pdf, sheet, pageIndex + 1);
        }

        pdf.save(`Cascade - ${sheet.name}.pdf`);
    };

    return renderPdf();
}
